import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, XCircle, Warning } from "@phosphor-icons/react";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { useT } from "../i18n";

const MP_KEY = process.env.REACT_APP_MP_PUBLIC_KEY;
let mpReady = false;
if (MP_KEY && !mpReady) {
  initMercadoPago(MP_KEY, { locale: "es-CL" });
  mpReady = true;
}

const fmtClp = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

const SUB_STYLES = {
  authorized: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-blue-50 text-blue-700 border-blue-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  none: "bg-slate-100 text-slate-600 border-slate-200",
};

export const BillingSection = ({ isAdmin }) => {
  const { t } = useT();
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => setSummary(null));
    api.get("/billing/payments").then((r) => setPayments(r.data)).catch(() => setPayments(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!summary || !summary.configured) return null;
  const sub = summary.subscription;
  const status = sub?.status || "none";

  const subscribe = async (formData) => {
    try {
      const { data } = await api.post("/billing/subscribe", {
        card_token_id: formData.token,
        payer_email: formData.payer?.email || formData.cardholderEmail || "",
      });
      toast.success(data.subscription?.status === "authorized" ? t("toast_subActivated") : t("toast_subPending"));
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
      throw e;
    }
  };

  const cancel = async () => {
    if (!window.confirm(t("cancelSubConfirm"))) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success(t("toast_subCancelled"));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-slate-200 p-5 space-y-4" data-testid="billing-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 flex items-center gap-1.5">
            <CreditCard size={14} /> {t("billing_title")}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">{t("billing_sub")}</p>
        </div>
        <span data-testid="subscription-status" className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${SUB_STYLES[status] || SUB_STYLES.none}`}>
          {t(`sub_${status}`)}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 border border-slate-200 divide-x divide-slate-200">
        <div className="px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("planLabel")}</p><p className="font-mono font-semibold text-slate-900 mt-0.5 capitalize" data-testid="billing-plan">{t(`plan_${summary.plan}`)}</p></div>
        <div className="px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("billing_seats")}</p><p className="font-mono font-semibold text-slate-900 mt-0.5" data-testid="billing-seats">{summary.seats}</p></div>
        <div className="px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("perUserMonth")}</p><p className="font-mono font-semibold text-slate-900 mt-0.5">{fmtClp(summary.price_per_user_clp)}</p></div>
        <div className="px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("billing_monthly")}</p><p className="font-mono font-semibold text-slate-900 mt-0.5" data-testid="billing-monthly-total">{fmtClp(summary.monthly_total_clp)}</p></div>
      </div>

      {summary.limited && (
        <div className="border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5" data-testid="free-limit-banner">
          <Warning size={17} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">{t("freeLimitBanner")} <b>{summary.seat_limit}</b> {t("freeLimitBanner2")}</p>
        </div>
      )}

      {sub?.next_payment_date && status === "authorized" && (
        <p className="text-xs text-slate-500">{t("nextPayment")}: <span className="font-mono">{new Date(sub.next_payment_date).toLocaleDateString()}</span> · {t("seatsAutoSync")}</p>
      )}

      {isAdmin && (status === "none" || status === "cancelled" || status === "paused") && (
        <Button data-testid="activate-subscription-btn" onClick={() => setPayOpen(true)} className="rounded-none bg-[#009ee3] hover:bg-[#0084bd] text-white">
          <CreditCard size={16} className="mr-1.5" /> {t("activateSub")}
        </Button>
      )}
      {isAdmin && (status === "authorized" || status === "pending") && (
        <Button data-testid="cancel-subscription-btn" onClick={cancel} disabled={busy} variant="outline" className="rounded-none border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300">
          <XCircle size={16} className="mr-1.5" /> {t("cancelSub")}
        </Button>
      )}

      {(status === "authorized" || status === "pending" || (payments && payments.length > 0)) && (
        <div data-testid="payments-history">
          <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2">{t("payments_title")}</p>
          {!payments || payments.length === 0 ? (
            <p className="text-xs text-slate-400">{t("noPayments")}</p>
          ) : (
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[480px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    {[t("th_date"), t("th_amount"), t("th_status"), t("th_operation")].map((h) => (
                      <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{p.date ? new Date(p.date).toLocaleDateString() : "—"}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-slate-900">{fmtClp(p.amount)}</td>
                      <td className="px-3 py-2 text-xs text-slate-600">{t(`pay_${p.status}`) !== `pay_${p.status}` ? t(`pay_${p.status}`) : p.status}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-400">{p.payment_id || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("payDialogTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            {t(`plan_${summary.plan}`)} · {summary.seats} {t("billing_seats").toLowerCase()} · <b className="font-mono">{fmtClp(summary.monthly_total_clp)}</b> / {t("perMonth")}
          </p>
          <div data-testid="mp-brick-container">
            {payOpen && (
              <CardPayment
                initialization={{ amount: summary.monthly_total_clp }}
                customization={{ paymentMethods: { maxInstallments: 1 } }}
                onSubmit={subscribe}
                onError={(e) => { console.error("MP Brick", e); }}
              />
            )}
          </div>
          <p className="text-[11px] text-slate-400">{t("mp_secure")}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
};
